'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { RoomInfoPanel } from '@/components/public/room-info-panel';
import { ImageLightbox } from '@/components/public/image-lightbox';
import type { LightboxImage } from '@/components/public/image-lightbox';
import type { Room } from '@/types/database';

export default function SmitheryContent({ room }: { room: Room }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const t = useTranslations('roomContent.smithery');

  const GALLERY_IMAGES: LightboxImage[] = [
    { src: '/design-images/rooms/smithery/detail.jpg', alt: t('alt.detail'), width: 1659, height: 2560 },
    { src: '/design-images/rooms/smithery/gallery-1.jpg', alt: t('alt.gallery1'), width: 1920, height: 1280 },
    { src: '/design-images/rooms/smithery/gallery-2.jpg', alt: t('alt.gallery2'), width: 1540, height: 2560 },
  ];

  return (
    <div id="room-content" className="w-full flex flex-col">
      {/* White spacer between hero and first section */}
      <div className="bg-white h-2 w-full" />

      {/* Section: Room Benefits */}
      <section className="bg-white w-full">
        <div className="max-w-[1500px] mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="w-full flex items-center py-6 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                    <h1 className="font-heading font-bold mb-4 text-4xl md:text-5xl">{t('benefits.titleLine1')}<br />{t('benefits.titleLine2')}</h1>
                    <p className="mb-6 text-base md:text-lg leading-relaxed text-gray-700">{t('benefits.p1')}</p>
                </div>
                <RoomInfoPanel room={room} />
              </div>
            </div>
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(0)}
            >
              <Image
                src="/design-images/rooms/smithery/detail.jpg"
                alt={t('alt.detail')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Section: Side-by-side */}
      <section className="bg-white w-full">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(1)}
            >
              <Image
                src="/design-images/rooms/smithery/gallery-1.jpg"
                alt={t('alt.gallery1')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
            <div className="w-full flex items-center py-8 md:py-12 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                    <h1 className="font-heading font-bold mb-6 text-4xl md:text-5xl">{t('story.titleLine1')}<br />{t('story.titleLine2')}</h1>
                    <div className="w-16 h-1 bg-[#801515] mb-8" />
                    <p className="mb-6 text-lg md:text-xl leading-relaxed font-light italic text-gray-700">{t('story.p1')}</p>
                    <p className="mb-6 text-base md:text-lg leading-relaxed text-gray-700">{t('story.p2')}</p>
                    <p className="mb-6 text-base md:text-lg leading-relaxed text-gray-700">{t('story.p3')}</p>
                    <p className="mb-8 text-base md:text-lg leading-relaxed text-gray-700">{t.rich('story.p4', { b: (chunks) => <strong>{chunks}</strong> })}</p>
                    <Link href="/activities#in-the-village" className="inline-flex items-center justify-center px-8 py-3 rounded-full text-sm font-bold uppercase tracking-wider transition-all duration-300 bg-[#801515] text-white hover:bg-[#601010] cursor-pointer">{t('story.cta')}</Link>
                </div>
              </div>
            </div>
        </div>
        
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Single portrait image */}
      <div className="mt-2 flex justify-center">
        <div className="w-1/2 cursor-pointer" onClick={() => setLightboxIndex(2)}>
          <Image
            src="/design-images/rooms/smithery/gallery-2.jpg"
            alt={t('alt.gallery2')}
            width={1540}
            height={2560}
            className="w-full h-auto"
            sizes="50vw"
          />
        </div>
      </div>

      {/* Fullscreen gallery lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={GALLERY_IMAGES}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
